import type { AxiosResponseHeaders } from 'axios';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { Promotion, PromotionStatus } from '@/models/promotion';
import { Property, Store, Banner } from '@/models/storetarget';
import { AvailableZonePlacement, ZonePlacement } from '@/models/zoneplacement';
import { parsePaginationHeaders } from '@/lib/utils';
import { parsePageQuery, parsePerPageQuery } from '@/utils';
import { http } from '@/utils/http';
import type { PaginatedResponse } from '@/utils/types';

interface PromotionParams {
  bannerIds: string[];
  page: string | null;
  perPage: string | null;
  search: string | null;
  sortBy: string | null;
  sortDir: string | null;
  status: string[];
  storeIds: string[];
  zoneIds: string[];
}

async function getPromotions(
  params: PromotionParams,
  zonePlacements: ZonePlacement[],
) {
  const page = parsePageQuery(params.page);
  const perPage = parsePerPageQuery(params.perPage);
  const { bannerIds, search, sortBy, sortDir, status, storeIds, zoneIds } =
    params;
  let url = `/promotions?page=${page}&page_size=${perPage}`;

  if (search) {
    url += `&search=${encodeURIComponent(search)}`;
  }

  if (sortBy) {
    url += `&sort_by=${encodeURIComponent(sortBy)}`;
  }

  if (sortDir) {
    url += `&sort_dir=${encodeURIComponent(sortDir)}`;
  }

  bannerIds.forEach((id) => {
    url += `&banner_ids[]=${encodeURIComponent(id)}`;
  });

  status.forEach((s) => {
    url += `&status[]=${encodeURIComponent(s)}`;
  });

  storeIds.forEach((id) => {
    url += `&store_ids[]=${encodeURIComponent(id)}`;
  });

  zoneIds.forEach((id) => {
    url += `&zone_ids[]=${encodeURIComponent(id)}`;
  });

  if (zoneIds.length > 0) {
    const placementIds =
      zoneIds.flatMap((id) => {
        const zone = zonePlacements.find((zone) => zone.id === parseInt(id));

        return zone?.placements?.map((p) => p.id) ?? [];
      }) ?? [];

    placementIds.forEach((placementId) => {
      url += `&placement_ids[]=${encodeURIComponent(placementId)}`;
    });
  }

  const { data, headers } = await http.get<Promotion[]>(url);
  const meta = parsePaginationHeaders(headers as AxiosResponseHeaders);

  return { data, meta } as PaginatedResponse<Promotion>;
}

export function usePromotions(
  params: PromotionParams,
  zonePlacements: ZonePlacement[],
) {
  return useQuery({
    queryFn: () => getPromotions(params, zonePlacements),
    queryKey: ['promotions', 'list', params, zonePlacements.map((zp) => zp.id)],
    placeholderData: keepPreviousData,
  });
}

async function getStores() {
  const { data } = await http.get<Store[]>('/promotions/filters/stores');

  return data;
}

export function useStores() {
  return useQuery({
    queryKey: ['promotions', 'stores'],
    queryFn: getStores,
  });
}

async function getBanners() {
  const { data } = await http.get<Banner[]>('/promotions/filters/banners');

  return data;
}

export function useBanners() {
  return useQuery({
    queryKey: ['promotions', 'banners'],
    queryFn: getBanners,
  });
}

async function getPlacements() {
  const { data } = await http.get<AvailableZonePlacement[]>(
    '/promotions/available_zone_placements',
  );

  return data;
}

function transformPlacements(data: AvailableZonePlacement[]) {
  return data.map((zp) => ({
    id: zp.id,
    name: zp.name,
    orientation: zp.orientation,
    ratio: zp.aspect_ratio,
    max_creative_duration_ms: zp.max_creative_duration_ms,
    placements: zp.placements,
  }));
}

export function usePlacements() {
  return useQuery({
    queryKey: ['promotions', 'zone-placements'],
    queryFn: getPlacements,
    select: transformPlacements,
  });
}

async function getProperties() {
  const { data } = await http.get<Property[]>('/promotions/store_properties');

  return data;
}

export function useProperties() {
  return useQuery({
    queryKey: ['promotions', 'properties'],
    queryFn: getProperties,
  });
}

async function getStatuses() {
  const { data } = await http.get<PromotionStatus[]>(
    '/promotions/filters/statuses',
  );

  return data;
}

export function useStatuses() {
  return useQuery({
    queryKey: ['promotions', 'statuses'],
    queryFn: getStatuses,
  });
}
